import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Settings, Users, ClipboardList, Plus, Trash2, ChevronLeft, ChevronRight, FileDown, BarChart3, Check, X, Coffee, CreditCard, ExternalLink, AlertCircle, Sparkles, Eye, EyeOff, RefreshCw, ShieldCheck } from "lucide-react";

const YASAMARU_ADMIN = "/yasamaru/thumbsup.png";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tokyo" });
  } catch { return "—"; }
}

function fmtHM(mins) {
  if (!mins) return "0:00";
  const h = Math.floor(mins / 60); const m = mins % 60;
  return `${h}:${m.toString().padStart(2, "0")}`;
}
function fmtYen(n) {
  return `¥${(n || 0).toLocaleString("ja-JP")}`;
}

export default function TenantAdminDashboard() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, tenant, logout, refresh } = useAuth();
  // Read ?tab=report from URL on initial mount
  const initialTab = (() => {
    if (typeof window === "undefined") return "attendance";
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["attendance", "employees", "report", "billing", "settings"].includes(t) ? t : "attendance";
  })();
  const [tab, setTab] = useState(initialTab); // attendance | employees | report | settings
  const [today, setToday] = useState(new Date().toISOString().slice(0, 10));
  const [daily, setDaily] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadDaily = async (day) => {
    try {
      const { data } = await api.get(`/tenant/attendance/daily`, { params: { day } });
      setDaily(data.items || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };
  const loadEmployees = async () => {
    try { const { data } = await api.get("/tenant/employees"); setEmployees(data.items || []); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };
  const loadSettings = async () => {
    try { const { data } = await api.get("/tenant/settings"); setSettings(data); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadDaily(today); }, [today]);
  useEffect(() => { loadEmployees(); loadSettings(); }, []);

  const onLogout = async () => { await logout(); nav(`/t/${slug}/login`); };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-stone-100 p-5">
        <div className="mb-2">
          <div className="text-xs text-stone-500">{tenant?.name}</div>
          <div className="font-display text-lg font-bold text-stone-900">管理画面</div>
        </div>
        <nav className="mt-6 space-y-1 flex-1">
          <SideBtn active={tab === "attendance"} onClick={() => setTab("attendance")} icon={<ClipboardList size={16} />} label="勤怠一覧" testid="tab-attendance" />
          <SideBtn active={tab === "employees"} onClick={() => setTab("employees")} icon={<Users size={16} />} label="従業員管理" testid="tab-employees" />
          <SideBtn active={tab === "report"} onClick={() => setTab("report")} icon={<BarChart3 size={16} />} label="月次レポート" testid="tab-report" />
          <SideBtn active={tab === "billing"} onClick={() => setTab("billing")} icon={<CreditCard size={16} />} label="お支払い" testid="tab-billing" />
          <SideBtn active={tab === "settings"} onClick={() => setTab("settings")} icon={<Settings size={16} />} label="設定" testid="tab-settings" />
        </nav>
        <img src={YASAMARU_ADMIN} alt="やさまる" className="w-24 mx-auto opacity-95 float-slow" />
        <div className="mt-auto space-y-2">
          <Link to={`/t/${slug}/`} className="block text-sm text-stone-600 hover:text-stone-900" data-testid="goto-clockin-link">→ 打刻画面へ</Link>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="w-full mt-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 px-4 py-2 text-sm font-semibold inline-flex items-center justify-center gap-2"><LogOut size={14} />ログアウト</button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 bg-white border-b border-stone-100 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-stone-500 truncate max-w-[55vw]">{tenant?.name}</div>
          <div className="font-display font-bold">管理画面</div>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/t/${slug}/`} className="px-3 py-1.5 rounded-full text-xs bg-emerald-100 text-emerald-800 font-semibold" data-testid="mobile-goto-clockin">打刻</Link>
          <button onClick={onLogout} data-testid="admin-logout-btn-mobile" className="p-2 rounded-full bg-stone-100"><LogOut size={16} /></button>
        </div>
      </div>

      <main className="flex-1 px-4 md:px-8 pt-20 md:pt-8 pb-12 max-w-6xl w-full">
        {/* mobile tabs */}
        <div className="md:hidden flex gap-2 mb-4 overflow-x-auto -mx-1 px-1 pb-1">
          {[
            ["attendance","勤怠一覧"],
            ["employees","従業員"],
            ["report","レポート"],
            ["billing","お支払い"],
            ["settings","設定"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`mobile-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 ${tab === k ? "bg-indigo-500 text-white" : "bg-white border border-stone-200 text-stone-700"}`}>{l}</button>
          ))}
        </div>

        {err && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm" data-testid="admin-error">{err}</div>}
        {msg && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5 rounded-xl text-sm" data-testid="admin-success">{msg}</div>}

        {tenant && tenant.subscription_status && tenant.subscription_status !== "active" && tenant.subscription_status !== "trialing" && (
          tenant.in_signup_trial ? (
            <div className="mb-4 bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 md:p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="trial-banner">
              <div className="flex items-start gap-3">
                <Sparkles className="text-emerald-500 mt-0.5 shrink-0" size={22} />
                <div>
                  <div className="font-bold text-emerald-900" data-testid="trial-days-left">
                    無料お試し中 ・ あと {tenant.signup_trial_days_left} 日
                  </div>
                  <div className="text-sm text-emerald-800 mt-0.5">
                    お試し期間中はすべての機能を無料でご利用いただけます。継続利用にはお支払いタブからカード登録をお願いします。
                  </div>
                </div>
              </div>
              <button onClick={() => nav(`/t/${slug}/billing/setup`)} data-testid="banner-goto-billing-btn"
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-2.5 text-sm inline-flex items-center gap-2 shrink-0">
                <CreditCard size={14} /> カード登録
              </button>
            </div>
          ) : (
            <div className="mb-4 bg-rose-50 border-2 border-rose-200 rounded-2xl p-4 md:p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="incomplete-billing-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-rose-500 mt-0.5 shrink-0" size={22} />
                <div>
                  <div className="font-bold text-rose-900">
                    {tenant.subscription_status === "past_due"
                      ? "お支払いが遅延しています"
                      : tenant.subscription_status === "canceled"
                        ? "サブスクリプションは解約済みです"
                        : "お試し期間が終了しました"}
                  </div>
                  <div className="text-sm text-rose-800 mt-0.5">
                    打刻・勤怠閲覧・月次レポートはご利用いただけません(スタッフ管理・設定は引き続きご利用可能です)。お支払いタブからプランを再開してください。
                  </div>
                </div>
              </div>
              <button onClick={() => nav(`/t/${slug}/billing/setup`)} data-testid="banner-goto-billing-btn"
                className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold px-5 py-2.5 text-sm inline-flex items-center gap-2 shrink-0">
                <CreditCard size={14} /> 設定する
              </button>
            </div>
          )
        )}

        {tab === "attendance" && (
          <AttendanceTab today={today} setToday={setToday} daily={daily} />
        )}
        {tab === "employees" && (
          <EmployeesTab employees={employees} reload={loadEmployees} setMsg={setMsg} setErr={setErr} myId={user?.id} />
        )}
        {tab === "report" && (
          <ReportTab setMsg={setMsg} setErr={setErr} settings={settings} />
        )}
        {tab === "billing" && (
          <BillingTab setMsg={setMsg} setErr={setErr} />
        )}
        {tab === "settings" && (
          <SettingsTab settings={settings} reload={loadSettings} setMsg={setMsg} setErr={setErr} refreshAuth={refresh} />
        )}
      </main>
    </div>
  );
}

function SideBtn({ active, onClick, icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium inline-flex items-center gap-2 transition-colors
      ${active ? "bg-indigo-50 text-indigo-800" : "text-stone-700 hover:bg-stone-50"}`}>
      <span className={active ? "text-indigo-500" : "text-stone-500"}>{icon}</span>{label}
    </button>
  );
}

function AttendanceTab({ today, setToday, daily }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <h1 className="font-display text-2xl font-black text-stone-900">勤怠一覧</h1>
        <input data-testid="daily-date-picker" type="date" value={today} onChange={(e) => setToday(e.target.value)} className="rounded-xl border border-stone-200 px-3 py-1.5 text-sm bg-white" />
      </div>
      {/* Desktop table */}
      <div className="card-soft p-4 md:p-6 hidden md:block">
        {daily.length === 0 ? <div className="text-stone-500 text-sm py-8 text-center">この日の打刻はまだありません。</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-stone-500">
                <tr><th className="text-left py-2 px-2">従業員</th><th className="text-left">出勤</th><th className="text-left">退勤</th><th className="text-left">休憩</th></tr>
              </thead>
              <tbody>
                {daily.map((r) => (
                  <tr key={r.id} className="border-t border-stone-100" data-testid={`admin-row-${r.user_id}`}>
                    <td className="py-2 px-2">
                      <div className="font-semibold text-stone-900">{r.user_name}</div>
                      <div className="text-xs text-stone-500">{r.user_email}</div>
                    </td>
                    <td className="text-emerald-700 font-semibold">{fmtTime(r.clock_in?.at)}</td>
                    <td className="text-rose-700 font-semibold">{fmtTime(r.clock_out?.at)}</td>
                    <td>{(r.breaks || []).length}回</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {daily.length === 0 ? <div className="card-soft p-8 text-center text-stone-500 text-sm">この日の打刻はまだありません。</div> : daily.map((r) => (
          <div key={r.id} className="card-soft p-4" data-testid={`admin-mobile-row-${r.user_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-stone-900">{r.user_name}</div>
                <div className="text-xs text-stone-500">{r.user_email}</div>
              </div>
              <span className="tag bg-stone-100 text-stone-700">休憩{(r.breaks || []).length}回</span>
            </div>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <div><div className="text-xs text-stone-500">出勤</div><div className="font-semibold text-emerald-700 tabular-nums">{fmtTime(r.clock_in?.at)}</div></div>
              <div className="w-px h-8 bg-stone-100" />
              <div><div className="text-xs text-stone-500">退勤</div><div className="font-semibold text-rose-700 tabular-nums">{fmtTime(r.clock_out?.at)}</div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmployeesTab({ employees, reload, setMsg, setErr, myId }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", hourly_wage: 1000 });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // user id
  const [editWage, setEditWage] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      const wage = Number(form.hourly_wage);
      const payload = {
        name: (form.name || "").trim(),
        role: form.role,
        hourly_wage: Number.isFinite(wage) && wage >= 0 ? wage : 0,
      };
      if (form.role === "tenant_admin") {
        payload.email = (form.email || "").trim();
        payload.password = form.password;
      }
      await api.post("/tenant/employees", payload);
      setForm({ name: "", email: "", password: "", role: "employee", hourly_wage: 1000 });
      setShowForm(false);
      setMsg("従業員を追加しました");
      reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("この従業員を削除しますか？")) return;
    try { await api.delete(`/tenant/employees/${id}`); reload(); setMsg("削除しました"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const startEdit = (u) => { setEditing(u.id); setEditWage(u.hourly_wage || 0); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/tenant/employees/${id}`, { hourly_wage: Number(editWage) });
      setEditing(null);
      setMsg("時給を更新しました");
      reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-black text-stone-900">従業員管理</h1>
          <div className="text-sm text-stone-500" data-testid="employee-count">{employees.length} / 30 名</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="toggle-add-employee" className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-1">
          <Plus size={14} /> 追加
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="card-soft p-5 mb-5 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="add-employee-form">
          <FormField label="氏名" required>
            <input required data-testid="new-emp-name" placeholder="山田 太郎" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" />
          </FormField>
          <FormField label="時給(円)" required>
            <input required data-testid="new-emp-wage" type="number" min="0" step="50" placeholder="1000" value={form.hourly_wage} onChange={(e) => setForm({ ...form, hourly_wage: e.target.value })} className="inp" />
          </FormField>
          <FormField label="役割" required>
            <select data-testid="new-emp-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="inp">
              <option value="employee">一般従業員</option>
              <option value="tenant_admin">管理者</option>
            </select>
          </FormField>
          {form.role === "tenant_admin" && (
            <>
              <FormField label="メールアドレス" required hint="管理者ログイン用">
                <input required data-testid="new-emp-email" type="email" placeholder="taro@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp"
                  inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
              </FormField>
              <FormField label="パスワード" required hint="6文字以上">
                <input required data-testid="new-emp-password" type="password" minLength={6} placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="inp" />
              </FormField>
            </>
          )}
          <div className="flex items-end md:col-span-1 lg:col-start-3">
            <button type="submit" data-testid="submit-new-emp" disabled={busy} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold px-4 py-2.5 text-sm">{busy ? "追加中..." : "追加する"}</button>
          </div>
          {form.role === "employee" && (
            <div className="md:col-span-2 lg:col-span-3 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
              💡 一般従業員は<strong>共有打刻画面で自分の名前を選んで打刻</strong>します。ログインIDは不要です。
            </div>
          )}
          <style>{`.inp{ padding:.5rem .75rem; border-radius:.625rem; border:1px solid #E7E5E4; background:#FAFAF9; outline:none; font-size:.875rem; width:100%;} .inp:focus{ background:#fff; box-shadow:0 0 0 3px rgba(129,140,248,.25);} `}</style>
        </form>
      )}

      {/* Desktop table */}
      <div className="card-soft p-4 md:p-6 hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-stone-500">
            <tr><th className="text-left py-2 px-2">氏名</th><th className="text-left">メール</th><th className="text-left">役割</th><th className="text-left">時給</th><th></th></tr>
          </thead>
          <tbody>
            {employees.map((u) => (
              <tr key={u.id} className="border-t border-stone-100" data-testid={`emp-row-${u.id}`}>
                <td className="py-2 px-2 font-semibold">{u.name}</td>
                <td>{u.email || <span className="text-stone-400 text-xs">（不要）</span>}</td>
                <td><span className={`tag ${u.role === "tenant_admin" ? "bg-indigo-100 text-indigo-800" : "bg-stone-100 text-stone-700"}`}>{u.role === "tenant_admin" ? "管理者" : "従業員"}</span></td>
                <td className="tabular-nums">
                  {editing === u.id ? (
                    <div className="inline-flex items-center gap-1">
                      <input type="number" min="0" step="50" value={editWage} onChange={(e) => setEditWage(e.target.value)} data-testid={`edit-wage-input-${u.id}`} className="w-24 px-2 py-1 rounded border border-stone-200 bg-white text-sm" />
                      <span className="text-xs text-stone-500">円</span>
                      <button onClick={() => saveEdit(u.id)} data-testid={`save-wage-${u.id}`} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check size={14} /></button>
                      <button onClick={() => setEditing(null)} className="p-1 text-stone-400 hover:bg-stone-100 rounded"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(u)} data-testid={`edit-wage-${u.id}`} className="text-stone-700 hover:text-indigo-600 cursor-pointer">
                      {fmtYen(u.hourly_wage)} <span className="text-xs text-stone-400">/ 時</span>
                    </button>
                  )}
                </td>
                <td className="text-right pr-2">
                  {u.id !== myId && (
                    <button data-testid={`delete-emp-${u.id}`} onClick={() => remove(u.id)} className="p-2 rounded-full hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {employees.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-stone-500">従業員はまだいません</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {employees.map((u) => (
          <div key={u.id} className="card-soft p-4" data-testid={`emp-mobile-${u.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-stone-900">{u.name}</div>
                <div className="text-xs text-stone-500">{u.email || "ログイン不要"}</div>
                <span className={`tag mt-1 ${u.role === "tenant_admin" ? "bg-indigo-100 text-indigo-800" : "bg-stone-100 text-stone-700"}`}>{u.role === "tenant_admin" ? "管理者" : "従業員"}</span>
              </div>
              {u.id !== myId && (
                <button onClick={() => remove(u.id)} className="p-2 rounded-full hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-stone-500">時給</span>
              {editing === u.id ? (
                <div className="inline-flex items-center gap-1">
                  <input type="number" min="0" step="50" value={editWage} onChange={(e) => setEditWage(e.target.value)} className="w-20 px-2 py-1 rounded border border-stone-200 bg-white text-sm" />
                  <button onClick={() => saveEdit(u.id)} className="p-1 text-emerald-600"><Check size={14} /></button>
                  <button onClick={() => setEditing(null)} className="p-1 text-stone-400"><X size={14} /></button>
                </div>
              ) : (
                <button onClick={() => startEdit(u)} className="font-semibold text-stone-800">{fmtYen(u.hourly_wage)}<span className="text-xs text-stone-400">/時</span></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportTab({ setMsg, setErr, settings }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detailUser, setDetailUser] = useState(null); // {user_id, name} or null

  const load = async () => {
    try {
      const { data } = await api.get(`/tenant/labor-cost`, { params: { year, month } });
      setReport(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  // Auto-open per-user detail modal if ?openUser=<id> is in URL (useful for direct linking / screenshots)
  useEffect(() => {
    if (typeof window === "undefined" || !report?.items?.length) return;
    const id = new URLSearchParams(window.location.search).get("openUser");
    if (!id) return;
    const target = report.items.find((it) => it.user_id === id) || report.items[0];
    if (target) setDetailUser({ user_id: target.user_id, name: target.name });
  }, [report]);

  // eslint-disable-next-line no-unused-vars
  const closeMonth = async () => {
    if (!window.confirm(`${year}年${month}月の休憩が記録されていない勤務日に、デフォルト休憩(${settings?.default_break_minutes || 0}分)を一括で適用します。よろしいですか？`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post(`/tenant/attendance/close-month`, { year, month });
      setMsg(`休憩の一括処理が完了しました：${data.updated}件に休憩(${data.minutes}分)を適用`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const downloadCsv = () => {
    // Use bearer token from localStorage in URL is not safe; instead use fetch then blob
    const token = localStorage.getItem("kintarou_token");
    fetch(`${API}/tenant/attendance/export?year=${year}&month=${month}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (r) => {
      if (!r.ok) throw new Error("ダウンロードに失敗しました");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yasamaru_time_${year}-${String(month).padStart(2, "0")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    }).catch((e) => setErr(e.message));
  };

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="font-display text-2xl font-black text-stone-900">月次レポート</h1>
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="prev-month-btn" className="rounded-full p-2 bg-white border border-stone-200 hover:bg-stone-50"><ChevronLeft size={18} /></button>
          <div className="font-display text-xl font-black text-stone-900 min-w-[8rem] text-center" data-testid="report-ym">{year}年{month}月</div>
          <button onClick={next} data-testid="next-month-btn" className="rounded-full p-2 bg-white border border-stone-200 hover:bg-stone-50"><ChevronRight size={18} /></button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-5">
        <Kpi label="人件費目安（合計）" value={fmtYen(report?.total_estimated_cost)} tone="emerald" big testid="kpi-cost" />
        <Kpi label="実労働（合計）" value={fmtHM(report?.total_worked_minutes) + " h"} tone="indigo" testid="kpi-hours" />
        <Kpi label="自動休憩（デフォ）" value={`${report?.default_break_minutes ?? 0} 分`} tone="orange" testid="kpi-break" />
      </div>

      {/* Actions */}
      <div className="card-soft p-4 md:p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-stone-700 text-sm">
          <FileDown size={16} className="text-stone-500" />
          <span>月次レポートをCSV形式でダウンロードできます。給与計算ソフト等に直接貼り付けてご利用ください。</span>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadCsv} data-testid="export-csv-btn" className="rounded-full bg-stone-900 hover:bg-stone-700 text-white font-semibold px-4 py-2 text-sm inline-flex items-center gap-1.5">
            <FileDown size={14} /> CSV出力
          </button>
        </div>
      </div>

      {/* Backup notice */}
      <div className="bg-amber-50/70 border border-amber-200 rounded-2xl p-3.5 mb-5 text-xs text-amber-900 leading-relaxed" data-testid="csv-backup-notice">
        <strong>⚠ 大切なお知らせ：</strong>
        労働基準法上の出勤簿・賃金台帳の保存義務は<strong>雇用主であるお客様</strong>に課されています。
        毎月の月次レポートはCSV出力で必ずバックアップを取り、自社で保管してください。
      </div>

      {/* Table (desktop) */}
      <div className="card-soft p-4 md:p-6 hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-stone-500">
            <tr>
              <th className="text-left py-2 px-2">従業員</th>
              <th className="text-left">勤務日数</th>
              <th className="text-left">実労働</th>
              <th className="text-left">時給</th>
              <th className="text-right pr-2">人件費目安</th>
            </tr>
          </thead>
          <tbody>
            {(report?.items || []).map((it) => (
              <tr key={it.user_id} className="border-t border-stone-100 hover:bg-stone-50/60 cursor-pointer" onClick={() => setDetailUser({ user_id: it.user_id, name: it.name })} data-testid={`report-row-${it.user_id}`}>
                <td className="py-2 px-2 font-semibold">
                  <span className="text-indigo-600 hover:underline" data-testid={`report-name-${it.user_id}`}>{it.name}</span>
                  <div className="text-xs text-stone-500 font-normal">{it.email}</div>
                </td>
                <td className="tabular-nums">{it.days} 日</td>
                <td className="tabular-nums">{fmtHM(it.worked_minutes)}</td>
                <td className="tabular-nums">{fmtYen(it.hourly_wage)}</td>
                <td className="tabular-nums text-right pr-2 font-bold text-emerald-700">{fmtYen(it.estimated_cost)}</td>
              </tr>
            ))}
            {(!report || report.items.length === 0) && (
              <tr><td colSpan={5} className="py-8 text-center text-stone-500">データがありません</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {(report?.items || []).map((it) => (
          <div key={it.user_id} className="card-soft p-4 cursor-pointer" onClick={() => setDetailUser({ user_id: it.user_id, name: it.name })} data-testid={`report-mobile-${it.user_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-indigo-700">{it.name}</div>
                <div className="text-xs text-stone-500">{it.email}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-stone-500">人件費目安</div>
                <div className="font-display font-black text-emerald-700">{fmtYen(it.estimated_cost)}</div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-stone-500">日数</div><div className="font-semibold">{it.days}日</div></div>
              <div><div className="text-stone-500">実労働</div><div className="font-semibold">{fmtHM(it.worked_minutes)}</div></div>
              <div><div className="text-stone-500">時給</div><div className="font-semibold">{fmtYen(it.hourly_wage)}</div></div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-stone-500 mt-4">※ 人件費は「実労働(分) × 時給 ÷ 60」で計算した概算目安です（実労働は分単位で集計）。深夜・残業・休日割増、社会保険料等は含まれません。<strong className="text-rose-700">正式な給与計算は給与計算ソフトで行ってください。</strong></p>

      {detailUser && (
        <UserMonthlyDetail
          userId={detailUser.user_id}
          userName={detailUser.name}
          year={year}
          month={month}
          settings={settings}
          onClose={() => { setDetailUser(null); load(); }}
          setMsg={setMsg}
          setErr={setErr}
        />
      )}
    </div>
  );
}

function fmtDtLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${dd}T${hh}:${mm}`;
  } catch { return ""; }
}

function localToIso(localStr) {
  if (!localStr) return "";
  try { return new Date(localStr).toISOString(); } catch { return ""; }
}

function UserMonthlyDetail({ userId, userName, year, month, settings, onClose, setMsg, setErr }) {
  const [records, setRecords] = useState([]);
  const [editing, setEditing] = useState(null); // record id
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", breaks: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/attendance/monthly", { params: { year, month, user_id: userId } });
      setRecords(data.items || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, year, month]);

  const startEdit = (r) => {
    setEditing(r.id);
    setEditForm({
      clock_in: fmtDtLocal((r.clock_in || {}).at),
      clock_out: fmtDtLocal((r.clock_out || {}).at),
      breaks: (r.breaks || []).map(b => ({ start: fmtDtLocal((b.start || {}).at), end: fmtDtLocal((b.end || {}).at) })),
    });
  };

  const saveEdit = async (id) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.put(`/tenant/attendance/${id}`, {
        clock_in: editForm.clock_in ? localToIso(editForm.clock_in) : "",
        clock_out: editForm.clock_out ? localToIso(editForm.clock_out) : "",
        breaks: editForm.breaks.map(b => ({ start_at: b.start ? localToIso(b.start) : null, end_at: b.end ? localToIso(b.end) : null })),
      });
      setEditing(null);
      setMsg("勤怠を更新しました");
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const closeMonthForUser = async () => {
    if (!window.confirm(`${userName}さんの ${year}年${month}月 の休憩が記録されていない勤務日に、自動休憩(${settings?.default_break_minutes || 0}分)を一括適用します。よろしいですか？`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/attendance/close-month", { year, month, user_id: userId });
      setMsg(`${data.updated}件に休憩(${data.minutes}分)を適用しました`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="user-monthly-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full my-8">
        <div className="sticky top-0 bg-white rounded-t-3xl px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <div className="text-xs text-stone-500">{year}年{month}月の勤怠</div>
            <div className="font-display text-xl font-black text-stone-900">{userName}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={closeMonthForUser} disabled={busy} data-testid="close-month-for-user-btn"
              className="rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
              <Coffee size={12} />この人の休憩一括処理
            </button>
            <button onClick={onClose} data-testid="close-detail-modal-btn" className="p-2 rounded-full hover:bg-stone-100"><X size={18} /></button>
          </div>
        </div>

        <div className="p-4 md:p-6">
          {records.length === 0 ? (
            <div className="text-center text-stone-500 py-10">この月の勤怠記録はありません</div>
          ) : (
            <div className="space-y-2">
              {records.map((r) => {
                const isEditing = editing === r.id;
                return (
                  <div key={r.id} className={`rounded-2xl border ${isEditing ? "border-indigo-300 bg-indigo-50/30" : "border-stone-100 bg-stone-50/50"} p-4`} data-testid={`detail-row-${r.date}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display font-bold text-stone-900">{r.date}</div>
                      {!isEditing ? (
                        <button onClick={() => startEdit(r)} data-testid={`edit-att-${r.id}`} className="text-xs text-indigo-600 hover:underline">編集</button>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => saveEdit(r.id)} disabled={busy} data-testid={`save-att-${r.id}`} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-1">保存</button>
                          <button onClick={() => setEditing(null)} className="rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold px-3 py-1">キャンセル</button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="grid md:grid-cols-2 gap-3">
                        <label className="block">
                          <span className="text-xs text-stone-600">出勤</span>
                          <input type="datetime-local" value={editForm.clock_in} onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })} data-testid={`edit-in-${r.id}`} className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm" />
                        </label>
                        <label className="block">
                          <span className="text-xs text-stone-600">退勤</span>
                          <input type="datetime-local" value={editForm.clock_out} onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })} data-testid={`edit-out-${r.id}`} className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm" />
                        </label>
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-stone-600">休憩</span>
                            <button onClick={() => setEditForm({ ...editForm, breaks: [...editForm.breaks, { start: "", end: "" }] })} className="text-xs text-indigo-600 hover:underline">＋休憩を追加</button>
                          </div>
                          <div className="space-y-2">
                            {editForm.breaks.map((b, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <input type="datetime-local" value={b.start} onChange={(e) => { const bs = [...editForm.breaks]; bs[i].start = e.target.value; setEditForm({ ...editForm, breaks: bs }); }} className="flex-1 px-2 py-1.5 rounded-lg border border-stone-200 text-xs" />
                                <span className="text-stone-400 text-xs">〜</span>
                                <input type="datetime-local" value={b.end} onChange={(e) => { const bs = [...editForm.breaks]; bs[i].end = e.target.value; setEditForm({ ...editForm, breaks: bs }); }} className="flex-1 px-2 py-1.5 rounded-lg border border-stone-200 text-xs" />
                                <button onClick={() => setEditForm({ ...editForm, breaks: editForm.breaks.filter((_, j) => j !== i) })} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={12} /></button>
                              </div>
                            ))}
                            {editForm.breaks.length === 0 && <div className="text-xs text-stone-400 italic">休憩なし</div>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 text-sm text-stone-700 flex-wrap">
                        <div><span className="text-xs text-stone-500">出勤</span><span className="ml-1 font-semibold text-emerald-700 tabular-nums">{fmtTime((r.clock_in || {}).at)}</span></div>
                        <div><span className="text-xs text-stone-500">退勤</span><span className="ml-1 font-semibold text-rose-700 tabular-nums">{fmtTime((r.clock_out || {}).at)}</span></div>
                        <div><span className="text-xs text-stone-500">休憩</span><span className="ml-1 tabular-nums">{(r.breaks || []).length}回</span></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BillingTab({ setMsg, setErr }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/billing/me"); setInfo(data); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => {
    load();
    // Detect successful redirect from Custom CSV checkout
    const params = new URLSearchParams(window.location.search);
    if (params.get("custom_csv") === "success") {
      setMsg("カスタムCSV出力オプションを購入しました！");
      // remove query without reloading
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
    }
    /* eslint-disable-next-line */
  }, []);

  const start = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/billing/checkout", { origin: window.location.origin });
      window.location.assign(data.url);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const openPortal = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/billing/portal", { origin: window.location.origin });
      window.location.assign(data.url);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const requestAddon = async (addon, label) => {
    if (!window.confirm(`「${label}」のお申込みを送信します。\n運営からの実装完了連絡後にご利用開始となります（連絡があるまで課金は発生しません）。\nよろしいですか？`)) return;
    setBusy(true); setErr("");
    try { await api.post("/billing/request-addon", { addon }); setMsg("お申込みを受け付けました。準備が整い次第ご連絡します。"); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const cancelAddonRequest = async (addon, label) => {
    if (!window.confirm(`「${label}」の申込みをキャンセルしますか？`)) return;
    setBusy(true); setErr("");
    try { await api.post("/billing/cancel-addon-request", { addon }); setMsg("お申込みをキャンセルしました"); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const toggleAddon = async (enable) => {
    if (!enable && !window.confirm("オプションを解除しますか？(月割計算で次回請求から)")) return;
    setBusy(true); setErr("");
    try { await api.post("/billing/addon", { enable }); setMsg("オプションを更新しました"); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const togglePin = async (enable) => {
    if (!enable && !window.confirm("「スタッフパスコード設定」オプションを解除しますか？(月割計算で次回請求から)")) return;
    setBusy(true); setErr("");
    try { await api.post("/billing/pin", { enable }); setMsg("オプションを更新しました"); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const buyCustomCsv = async () => {
    if (!window.confirm("カスタムCSV出力オプション(¥15,000、一回のお支払い)を購入します。\n決済画面に移動します。よろしいですか？")) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/billing/custom-csv/checkout", { origin: window.location.origin });
      window.location.assign(data.url);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  const status = info?.subscription_status || "incomplete";
  const isActive = status === "active" || status === "trialing";
  const sub = info?.subscription;

  return (
    <div>
      <h1 className="font-display text-2xl font-black text-stone-900 mb-5">お支払い</h1>

      {/* Status card */}
      <div className="card-soft p-6 md:p-7 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-stone-500">サブスクリプション状況</div>
            <div className="font-display font-black text-2xl text-stone-900 mt-1" data-testid="sub-status">
              {status === "trialing" && <span className="text-emerald-600">無料お試し中</span>}
              {status === "active" && <span className="text-emerald-600">有効</span>}
              {status === "past_due" && <span className="text-rose-600">支払い遅延</span>}
              {status === "canceled" && <span className="text-stone-500">解約済み</span>}
              {status === "incomplete" && <span className="text-amber-600">未登録</span>}
            </div>
            {sub?.current_period_end && (
              <div className="text-sm text-stone-600 mt-1">
                次回更新日: {new Date(sub.current_period_end * 1000).toLocaleDateString("ja-JP")}
                {sub.cancel_at_period_end && <span className="ml-2 text-rose-600 text-xs">(期間終了時に解約予定)</span>}
              </div>
            )}
          </div>
          {!isActive && (
            <button onClick={start} disabled={busy} data-testid="start-billing-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold px-5 py-2.5 inline-flex items-center gap-2"><CreditCard size={16} />2週間無料を開始</button>
          )}
        </div>

        {sub?.items?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100 space-y-1.5 text-sm">
            {sub.items.map((it) => (
              <div key={it.price_id} className="flex justify-between" data-testid={`sub-item-${it.nickname || it.price_id}`}>
                <span className="text-stone-600">{it.nickname === "base_monthly" ? "通常プラン" : it.nickname === "addon_multi_monthly" ? "複数拠点・部署分けオプション" : it.nickname === "addon_pin_monthly" ? "スタッフパスコード設定オプション" : it.nickname || "オプション"}</span>
                <span className="font-semibold tabular-nums">¥{it.amount?.toLocaleString()} / 月</span>
              </div>
            ))}
          </div>
        )}

        {status === "past_due" && (
          <div className="mt-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-sm inline-flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" />お支払いが完了していません。カスタマーポータルからカード情報を更新してください。
          </div>
        )}
      </div>

      {/* Add-on: multi-location */}
      <div className="card-soft p-5 md:p-6 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="addon-toggle-card">
        <div>
          <div className="font-display font-bold text-stone-900 inline-flex items-center gap-2">
            複数拠点・部署分け オプション
            {info?.has_addon && <span className="tag bg-emerald-100 text-emerald-800">ご利用中</span>}
            {!info?.has_addon && info?.pending_addons?.multi && <span className="tag bg-amber-100 text-amber-800">申込み受付中</span>}
          </div>
          <div className="text-sm text-stone-600 mt-1">店舗・部署ごとに従業員と勤怠を分けて管理 (+¥800 / 月 税別)</div>
          {info?.pending_addons?.multi && !info?.has_addon && (
            <div className="text-xs text-amber-700 mt-1">※ お申込みを受け付けています。準備が整い次第、運営よりご連絡いたします（連絡があるまで課金は発生しません）。</div>
          )}
          {!isActive && !info?.has_addon && !info?.pending_addons?.multi && (
            <div className="text-xs text-amber-700 mt-1">※ サブスク開始後にお申込みいただけます</div>
          )}
        </div>
        {info?.has_addon ? (
          <button onClick={() => toggleAddon(false)} disabled={busy} data-testid="toggle-addon-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-50">
            解除する
          </button>
        ) : info?.pending_addons?.multi ? (
          <button onClick={() => cancelAddonRequest("multi", "複数拠点・部署分け")} disabled={busy} data-testid="cancel-addon-request-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50">
            申込みをキャンセル
          </button>
        ) : (
          <button onClick={() => requestAddon("multi", "複数拠点・部署分け")} disabled={busy || !isActive} data-testid="request-addon-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed">
            申込む
          </button>
        )}
      </div>

      {/* Add-on: Staff PIN */}
      <div className="card-soft p-5 md:p-6 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="pin-toggle-card">
        <div>
          <div className="font-display font-bold text-stone-900 inline-flex items-center gap-2">
            スタッフパスコード設定 オプション
            {info?.has_staff_pin && <span className="tag bg-emerald-100 text-emerald-800">ご利用中</span>}
            {!info?.has_staff_pin && info?.pending_addons?.pin && <span className="tag bg-amber-100 text-amber-800">申込み受付中</span>}
          </div>
          <div className="text-sm text-stone-600 mt-1">各スタッフに4桁のパスコードを設定して、なりすまし打刻を防止 (+¥300 / 月 税別)</div>
          {info?.pending_addons?.pin && !info?.has_staff_pin && (
            <div className="text-xs text-amber-700 mt-1">※ お申込みを受け付けています。準備が整い次第、運営よりご連絡いたします（連絡があるまで課金は発生しません）。</div>
          )}
          {!isActive && !info?.has_staff_pin && !info?.pending_addons?.pin && (
            <div className="text-xs text-amber-700 mt-1">※ サブスク開始後にお申込みいただけます</div>
          )}
        </div>
        {info?.has_staff_pin ? (
          <button onClick={() => togglePin(false)} disabled={busy} data-testid="toggle-pin-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-50">
            解除する
          </button>
        ) : info?.pending_addons?.pin ? (
          <button onClick={() => cancelAddonRequest("pin", "スタッフパスコード設定")} disabled={busy} data-testid="cancel-pin-request-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50">
            申込みをキャンセル
          </button>
        ) : (
          <button onClick={() => requestAddon("pin", "スタッフパスコード設定")} disabled={busy || !isActive} data-testid="request-pin-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed">
            申込む
          </button>
        )}
      </div>

      {/* Custom CSV one-time purchase */}
      <div className="card-soft p-5 md:p-6 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="custom-csv-card">
        <div>
          <div className="font-display font-bold text-stone-900 inline-flex items-center gap-2">
            カスタムCSV出力 オプション
            {info?.has_custom_csv && <span className="tag bg-emerald-100 text-emerald-800">ご利用中</span>}
            {!info?.has_custom_csv && info?.pending_addons?.csv && <span className="tag bg-amber-100 text-amber-800">申込み受付中</span>}
          </div>
          <div className="text-sm text-stone-600 mt-1">出力項目を自由にカスタマイズできるCSVオプション (¥15,000 / 一回のお支払い)</div>
          {info?.pending_addons?.csv && !info?.has_custom_csv && (
            <div className="text-xs text-amber-700 mt-1">※ お申込みを受け付けています。お見積もり・お支払いのご案内を運営よりお送りいたします。</div>
          )}
        </div>
        {info?.has_custom_csv ? (
          <span className="text-sm text-emerald-700 font-semibold">ご利用いただけます</span>
        ) : info?.pending_addons?.csv ? (
          <button onClick={() => cancelAddonRequest("csv", "カスタムCSV出力")} disabled={busy} data-testid="cancel-csv-request-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50">
            申込みをキャンセル
          </button>
        ) : (
          <button onClick={() => requestAddon("csv", "カスタムCSV出力")} disabled={busy} data-testid="request-csv-btn"
            className="rounded-full font-bold px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">
            申込む
          </button>
        )}
      </div>

      {/* Actions */}
      {info?.stripe_customer_id && (
        <div className="card-soft p-5 md:p-6 mb-4">
          <div className="font-display font-bold text-stone-900 mb-2">カード・領収書・解約</div>
          <p className="text-sm text-stone-600 mb-3">カスタマーポータルから、カード情報の変更・領収書のダウンロード・解約手続きが行えます。</p>
          <button onClick={openPortal} disabled={busy} data-testid="open-portal-btn" className="rounded-full bg-stone-900 hover:bg-stone-700 disabled:opacity-50 text-white font-semibold px-5 py-2.5 inline-flex items-center gap-2">
            カスタマーポータルへ <ExternalLink size={14} />
          </button>
        </div>
      )}

      {/* Recent invoices */}
      {info?.invoices && info.invoices.length > 0 && (
        <div className="card-soft p-4 md:p-6">
          <div className="font-display font-bold text-stone-900 mb-3">最近の請求書</div>
          <table className="w-full text-sm">
            <thead className="text-stone-500"><tr><th className="text-left py-2 px-2">日付</th><th className="text-left">番号</th><th className="text-left">金額</th><th className="text-left">状態</th><th></th></tr></thead>
            <tbody>
              {info.invoices.map((i) => (
                <tr key={i.id} className="border-t border-stone-100" data-testid={`invoice-${i.id}`}>
                  <td className="py-2 px-2">{new Date(i.created * 1000).toLocaleDateString("ja-JP")}</td>
                  <td className="text-stone-600">{i.number || "—"}</td>
                  <td className="tabular-nums">¥{(i.amount_paid || i.amount_due).toLocaleString()}</td>
                  <td><span className={`tag ${i.status === "paid" ? "bg-emerald-100 text-emerald-800" : i.status === "open" ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-700"}`}>{i.status}</span></td>
                  <td className="text-right pr-2">
                    {i.invoice_pdf && <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">PDF</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function FormField({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-stone-700">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      {hint && <span className="block text-xs text-stone-500 mb-1">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Kpi({ label, value, tone, big, testid }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
    orange: "bg-orange-50 text-orange-700 border-orange-100",
  }[tone] || "bg-stone-50 text-stone-700 border-stone-100";
  return (
    <div className={`rounded-2xl border ${colors} p-4 md:p-5`} data-testid={testid}>
      <div className="text-xs">{label}</div>
      <div className={`font-display font-black ${big ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"} mt-1`}>{value}</div>
    </div>
  );
}

function SettingsTab({ settings, reload, setMsg, setErr, refreshAuth }) {
  const [companyName, setCompanyName] = useState("");
  const [allow, setAllow] = useState(false);
  const [breakMin, setBreakMin] = useState(60);
  const [roundingIn, setRoundingIn] = useState("none");
  const [roundingOut, setRoundingOut] = useState("none");
  const [roundingMinutes, setRoundingMinutes] = useState(15);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings) {
      setCompanyName(settings.name || "");
      setAllow(!!settings.allow_impersonation);
      setBreakMin(settings.default_break_minutes ?? 0);
      setRoundingIn(settings.rounding_mode_in || "none");
      setRoundingOut(settings.rounding_mode_out || "none");
      setRoundingMinutes(settings.rounding_minutes ?? 15);
    }
  }, [settings]);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.put("/tenant/settings", {
        company_name: companyName,
        allow_impersonation: allow,
        default_break_minutes: Number(breakMin),
        rounding_mode_in: roundingIn,
        rounding_mode_out: roundingOut,
        rounding_minutes: Number(roundingMinutes),
      });
      setMsg("設定を保存しました");
      reload();
      refreshAuth && refreshAuth();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="text-stone-500">読み込み中...</div>;

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl font-black text-stone-900 mb-5">設定</h1>

      <div className="card-soft p-6 mb-5">
        <label className="block">
          <span className="text-sm font-semibold text-stone-700">会社名</span>
          <input data-testid="settings-company-name" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-indigo-300 outline-none" />
        </label>
      </div>

      {/* Punch URL share */}
      <div className="card-soft p-6 mb-5 border-2 border-emerald-200 bg-emerald-50/30">
        <div className="font-semibold text-stone-700 text-sm mb-1">📱 打刻画面のURL（共有）</div>
        <p className="text-xs text-stone-500 mb-3">店舗のタブレットやPCで以下のURLを開くと、スタッフが自分の名前を選んで打刻できます。ログイン不要。</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white border border-emerald-200 px-3 py-2.5 rounded-xl text-sm text-stone-800 truncate" data-testid="punch-url-display">
            {typeof window !== "undefined" ? window.location.origin : "https://yasamarutime.online"}/t/{settings.slug}/punch
          </code>
          <button onClick={() => { const url = `${window.location.origin}/t/${settings.slug}/punch`; navigator.clipboard?.writeText(url); setMsg("打刻URLをコピーしました"); }} data-testid="copy-punch-url-btn"
            className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2.5 text-sm shrink-0">
            コピー
          </button>
          <a href={`/t/${settings.slug}/punch`} target="_blank" rel="noreferrer" data-testid="open-punch-btn"
            className="rounded-xl bg-stone-900 hover:bg-stone-700 text-white font-semibold px-4 py-2.5 text-sm shrink-0">
            開く
          </a>
        </div>
        <div className="mt-3 text-xs text-stone-500">管理者ログインURL: <code className="bg-stone-100 px-1.5 py-0.5 rounded">{typeof window !== "undefined" ? window.location.origin : ""}/login</code></div>
      </div>

      {/* Terminal passcode */}
      <TerminalPasscodeCard settings={settings} reload={reload} setMsg={setMsg} setErr={setErr} />

      <div className="card-soft p-6 mb-5">
        <label className="block">
          <span className="text-sm font-semibold text-stone-700">自動休憩の分数（休憩一括処理で適用）</span>
          <p className="text-xs text-stone-500 mt-1">初期値は <strong>0分</strong>（自動休憩なし）です。0より大きい値を設定すると、休憩を記録し忘れた勤務日に対し「休憩一括処理」ボタンでこの分数の休憩を後から一括適用できます。月次レポートの人件費計算でも、休憩が記録されていない日にはこの分数を差し引きます。</p>
          <div className="mt-2 flex items-center gap-2">
            <input data-testid="settings-break-minutes" type="number" min="0" step="5" max="240" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} className="w-28 px-3.5 py-2.5 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-indigo-300 outline-none" />
            <span className="text-sm text-stone-600">分</span>
          </div>
        </label>
      </div>

      <div className="card-soft p-6 mb-5">
        <div className="font-semibold text-stone-700 text-sm mb-1">打刻時刻の丸め</div>
        <p className="text-xs text-stone-500 mb-3">出勤・退勤の打刻時刻をそれぞれ独立して丸めます。労働時間は丸めた時刻同士の差で計算されます。</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs text-stone-600">出勤の丸め方</span>
            <select data-testid="settings-rounding-in" value={roundingIn} onChange={(e) => setRoundingIn(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-indigo-300 outline-none text-sm">
              <option value="none">丸めなし</option>
              <option value="up">切り上げ（遅めに）</option>
              <option value="down">切り捨て（早めに）</option>
              <option value="nearest">四捨五入</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-stone-600">退勤の丸め方</span>
            <select data-testid="settings-rounding-out" value={roundingOut} onChange={(e) => setRoundingOut(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-indigo-300 outline-none text-sm">
              <option value="none">丸めなし</option>
              <option value="up">切り上げ（遅めに）</option>
              <option value="down">切り捨て（早めに）</option>
              <option value="nearest">四捨五入</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-stone-600">単位(分)</span>
            <select data-testid="settings-rounding-minutes" value={roundingMinutes} onChange={(e) => setRoundingMinutes(e.target.value)} disabled={roundingIn === "none" && roundingOut === "none"} className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-indigo-300 outline-none text-sm disabled:opacity-50">
              <option value="1">1分</option>
              <option value="5">5分</option>
              <option value="10">10分</option>
              <option value="15">15分</option>
              <option value="30">30分</option>
            </select>
          </label>
        </div>
        <div className="mt-3 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs text-stone-600">
          <strong className="text-stone-800">例</strong>(15分単位):
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            <li>9:07出勤を「切り上げ」 → 9:15として計算</li>
            <li>18:07退勤を「切り捨て」 → 18:00として計算</li>
            <li>結果: 8時間45分労働 (休憩を引く前の値)</li>
          </ul>
        </div>
      </div>

      <div className="card-soft p-6 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display font-bold text-stone-900">代理ログインの許可</div>
            <p className="text-sm text-stone-600 mt-1">運営者が、サポート対応のためにあなたの企業のユーザーとしてログインすることを許可します。</p>
          </div>
          <button data-testid="impersonation-toggle" onClick={() => setAllow(!allow)}
            className={`w-12 h-7 rounded-full p-1 transition-colors ${allow ? "bg-indigo-500" : "bg-stone-300"}`} aria-pressed={allow}>
            <span className={`block w-5 h-5 bg-white rounded-full transition-transform ${allow ? "translate-x-5" : ""}`} />
          </button>
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="save-settings-btn" className="rounded-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-bold px-6 py-2.5">
        {busy ? "保存中..." : "設定を保存"}
      </button>
    </div>
  );
}

function TerminalPasscodeCard({ settings, reload, setMsg, setErr }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(settings?.terminal_passcode || "");

  useEffect(() => { setCode(settings?.terminal_passcode || ""); }, [settings?.terminal_passcode]);

  const regen = async () => {
    if (!window.confirm("打刻端末のパスコードを再発行します。\n\n再発行すると、すべての店舗端末で再認証が必要になります（退職者対策などに使用してください）。\n続行しますか？")) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/tenant/terminal-passcode/regenerate");
      setCode(data.terminal_passcode);
      setShow(true);
      setMsg("パスコードを再発行しました。各店舗端末で新しいパスコードを入力してください。");
      reload && reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard?.writeText(code);
    setMsg("パスコードをコピーしました");
  };

  const masked = code ? "••••••" : "未設定";

  return (
    <div className="card-soft p-6 mb-5 border-2 border-amber-200 bg-amber-50/30" data-testid="terminal-passcode-card">
      <div className="flex items-start gap-2 mb-2">
        <ShieldCheck className="text-amber-600 mt-0.5 shrink-0" size={18} />
        <div className="flex-1">
          <div className="font-semibold text-stone-800 text-sm">🔒 打刻端末のパスコード（セキュリティ）</div>
          <p className="text-xs text-stone-600 mt-1 leading-relaxed">
            打刻画面 (<code>/t/{settings.slug}/punch</code>) は、URLを知っているだけでは開けません。
            各店舗のタブレットやPCで、最初に1回だけ<strong>6桁のパスコード</strong>を入力していただきます。一度入力すれば、その端末では次回から不要です。
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[180px] bg-white border-2 border-amber-300 rounded-2xl px-5 py-3 font-display font-black text-3xl tracking-[0.4em] tabular-nums text-center text-stone-900" data-testid="passcode-display">
          {show ? code : masked}
        </div>
        <button onClick={() => setShow(!show)} data-testid="toggle-passcode-visibility"
          className="rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-semibold px-4 py-3 text-sm inline-flex items-center gap-1.5">
          {show ? <><EyeOff size={14} /> 隠す</> : <><Eye size={14} /> 表示</>}
        </button>
        <button onClick={copy} disabled={!code} data-testid="copy-passcode-btn"
          className="rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold px-4 py-3 text-sm">
          コピー
        </button>
        <button onClick={regen} disabled={busy} data-testid="regenerate-passcode-btn"
          className="rounded-xl bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-semibold px-4 py-3 text-sm inline-flex items-center gap-1.5">
          <RefreshCw size={14} /> 再発行
        </button>
      </div>
      <div className="mt-3 text-xs text-stone-500 leading-relaxed">
        <strong>運用のコツ:</strong> 店舗端末に最初に設定する際は管理者が直接入力してください。スタッフには伝えなくてOKです。<br />
        退職者が出た場合や、パスコードが漏れた疑いがある場合は<strong>「再発行」</strong>を押すと、全端末で再認証が必要になります。
      </div>
    </div>
  );
}
